import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Country, LegalForm } from "@/api/data-contracts";
import { useLegalFormsByCountry } from "@/datacaches";
import { needsLegalFormReselectionForCountryChange } from "@/model/legalForm";
import { useSnackbarEx } from "@/lib/snackbarex";
import { CommonI18N } from "@/translations";
import { DataCacheStatus } from "@/lib/datacache";

export type GuidedLegalFormCountryChangeParams = {
    /** ID of the edited address — the combined update is skipped while it is not yet known. */
    addressId: number | undefined;
    /** Whether the edited address is the company address — the guided flow only applies there. */
    isCompanyAddress: boolean | undefined;
    /** Country originally loaded onto the address (the unmodified pre-edit value). */
    originalCountry: Country | undefined;
    /** Country currently selected in the edit form. */
    newCountry: Country | undefined;
    /** Legal form currently stored on the company. */
    currentLegalForm: LegalForm | undefined;
    /** useModelState validate — run before the follow-up dialog opens (see handleSave). */
    validate: () => boolean | undefined;
    /** useModelState save — the plain save path; returns null when client-side validation fails. */
    save: () => Promise<unknown> | null;
    /** Calls the combined atomic address+legal-form endpoint for the given address. */
    confirmUpdate: (addressId: number, legalForm: LegalForm) => Promise<unknown>;
    /** Invoked after a successful save on either path — close the dialog, refresh the caller. */
    onSaved: () => void;
};

export type GuidedLegalFormCountryChange = {
    legalFormDialogOpen: boolean;
    closeLegalFormDialog: () => void;
    handleSave: () => Promise<void>;
    handleLegalFormConfirm: (legalForm: LegalForm) => Promise<void>;
    /**
     * True while a save request (plain or combined) is in flight. The address save geocodes
     * server-side via an external service and can take noticeably long — callers use this to show
     * a busy indicator on their Save buttons and to suppress double submits.
     */
    saving: boolean;
    legalFormsByCountry: Record<string, LegalForm[]>;
    legalFormsByCountryStatus: DataCacheStatus;
};

/**
 * Shared guided-flow logic for the two address-edit dialogs (company detail page and admin address
 * page): a company-address country change that no longer matches the stored legal form cannot be
 * saved as-is (the server rejects it) — instead of calling save(), handleSave opens the
 * LegalFormForCountryDialog and handleLegalFormConfirm persists country and legal form atomically
 * via the caller-supplied combined endpoint. Keeping this in one hook means every guided-flow
 * correction lands in both dialogs at once.
 */
export const useGuidedLegalFormCountryChange = ({
    addressId,
    isCompanyAddress,
    originalCountry,
    newCountry,
    currentLegalForm,
    validate,
    save,
    confirmUpdate,
    onSaved
}: GuidedLegalFormCountryChangeParams): GuidedLegalFormCountryChange => {
    const { t } = useTranslation();
    const { showMessage } = useSnackbarEx();
    const [legalFormsByCountry, legalFormsByCountryStatus] = useLegalFormsByCountry();
    const [legalFormDialogOpen, setLegalFormDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (saving) {
            return;
        }
        // Gated on an actual country change (originalCountry vs. newCountry) and on the reference
        // cache being fully "loaded" — before that, the map is still empty and the check would
        // wrongly treat every stored legal form as incompatible, opening the guided dialog with no
        // selectable options (UI dead-end). Falling through to the plain save() is safe either way:
        // the server-side reject guard still refuses a genuinely incompatible combination.
        if (
            isCompanyAddress &&
            legalFormsByCountryStatus === "loaded" &&
            needsLegalFormReselectionForCountryChange(
                originalCountry,
                newCountry,
                currentLegalForm,
                legalFormsByCountry
            )
        ) {
            // The guided path bypasses save() — the only place validate() otherwise runs — so run
            // it explicitly here: other edited fields must still be validated and their field
            // errors shown before the atomic update is even attempted.
            if (!validate()) {
                return;
            }
            setLegalFormDialogOpen(true);
            return;
        }
        // save() returns null when client-side validation fails (no request is sent).
        const result = save();
        if (!result) {
            return;
        }
        setSaving(true);
        try {
            await result;
            onSaved();
        } catch {
            // useModelState.save() already shows the backend message via the shared snackbar
            // infrastructure and rethrows — catching here only prevents an unhandled promise
            // rejection; the dialog stays open so the operator can correct the input.
        } finally {
            setSaving(false);
        }
    };

    const handleLegalFormConfirm = async (legalForm: LegalForm) => {
        if (addressId == null || saving) {
            return;
        }
        setSaving(true);
        try {
            await confirmUpdate(addressId, legalForm);
            setLegalFormDialogOpen(false);
            onSaved();
        } catch (error: unknown) {
            // The combined endpoint is called directly (not through useModelState.save()), so the
            // failure must be surfaced explicitly. Both dialogs stay open so the operator can
            // correct the input or pick another legal form.
            showMessage({
                summary: t(CommonI18N.message.saveFailed),
                error,
                severity: "error"
            });
        } finally {
            setSaving(false);
        }
    };

    return {
        legalFormDialogOpen,
        closeLegalFormDialog: () => setLegalFormDialogOpen(false),
        handleSave,
        handleLegalFormConfirm,
        saving,
        legalFormsByCountry,
        legalFormsByCountryStatus
    };
};
